import { CircularProgress, Slide } from '@mui/material';
import FlowRunLogs from 'js/components/data_flows/cards/FlowRunLogs';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import { FlowLogsView } from 'js/components/pipelines/list/FlowLogsView';
import { Box } from 'new/elements/Box';
import { Button } from 'new/elements/Button';
import Portal from 'new/elements/Portal';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { isEqual } from 'lodash';
import React, { memo, useCallback, useState } from 'react';
import classes from '../FlowNodes.module.scss';
import styles from './NexsetLoaderNode.module.scss';

type ExploreFlowLogs = {
  originNodeId: string;
  flowNodeId: number;
  connectorType: string;
} | null;

const FLOW_RUN_LOGS_CONFIG = {
  omitRunSelector: true,
  skipClearLogsOnUnmount: true,
  autoRefreshIntervalValue: 3000, // 3 seconds
  omitNavigationPanelControls: false,
};

function NexsetLoaderNode(props: any) {
  const { data } = props;
  const { source, logsOnlyFocused, selectedFlow, parentId, isSqlAutomation } = data;
  const { expandedSources } = useExpandedNodes();
  const { refreshPipelineById } = usePipelinesAdapter();
  const isFlowInsightsV2Enabled = useFeatureFlag('UI_ENABLE_FLOW_INSIGHTS_V2');

  const [nexsetError, setNexsetError] = useState(false);
  const [exploreMode, setExploreMode] = useState<ExploreFlowLogs>(null);
  const selected = parentId?.includes(selectedFlow);

  // TODO: Nodes shouldn't return null because that breaks the fitView
  // functionality. Instead, incorporate this condition into
  // shouldCreateNexsetLoader().
  if (logsOnlyFocused && !selected) return null;

  const onDataSetFound = useCallback(
    () => refreshPipelineById(source.origin_node_id),
    [source.origin_node_id, refreshPipelineById],
  );

  const onDataSetError = useCallback(() => setNexsetError(true), []);

  const onExploreFlowLogs = useCallback(() => {
    setExploreMode({
      originNodeId: source.origin_node_id,
      flowNodeId: source.flow_node_id,
      connectorType: source.connector_type,
    });
  }, [source.origin_node_id, source.flow_node_id, source.connector_type]);

  const onCloseExploreFlowLogs = useCallback(() => {
    setExploreMode(null);
  }, []);

  return (
    <>
      <Box className={classes.nexsetLoaderBox}>
        <Stack width="450px" gap="8px">
          {isSqlAutomation ? (
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px">
              <Typography className={classes.nexsetLoaderTitle}>Recent Execution Logs</Typography>

              {isFlowInsightsV2Enabled ? (
                <Button onClick={onExploreFlowLogs} variant="contained">
                  Open Expanded View
                </Button>
              ) : null}
            </Stack>
          ) : nexsetError ? null : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap="8px">
                <Stack direction="row" alignItems="center" gap="8px">
                  <CircularProgress size={16} color="inherit" />
                  <Typography className={classes.nexsetLoaderTitle}>
                    Scanning Source for Nexsets ...
                  </Typography>
                </Stack>

                {isFlowInsightsV2Enabled ? (
                  <Button onClick={onExploreFlowLogs} variant="contained">
                    Open Expanded View
                  </Button>
                ) : null}
              </Stack>

              <Typography className={classes.nexsetLoaderDesc}>
                Nexla's Nexset detection eliminates the need for a single data type in your flow.
                The source is automatically scanned, and any new Nexsets are displayed, saving you
                time and effort.
              </Typography>
            </>
          )}

          <Stack>
            <FlowLogsView
              scanningMode={true}
              dataSource={source}
              disablePolling={!!exploreMode}
              onDataSetFound={isSqlAutomation ? undefined : onDataSetFound}
              onDataSetError={isSqlAutomation ? undefined : onDataSetError}
            />
          </Stack>
        </Stack>
      </Box>

      {exploreMode && (
        <Portal>
          <div onClick={onCloseExploreFlowLogs} className={`modal-fader ${styles.modalBackdrop}`} />
          <Slide direction="left" in={Boolean(exploreMode)}>
            <div className={styles.drawerContainer}>
              <FlowRunLogs
                exploreFlowLogs
                runId={null}
                createdAt={source?.created_at}
                flowNodeId={exploreMode.flowNodeId}
                originNodeId={exploreMode.originNodeId}
                connectorType={exploreMode.connectorType}
                onClose={onCloseExploreFlowLogs}
                config={FLOW_RUN_LOGS_CONFIG}
              />
            </div>
          </Slide>
        </Portal>
      )}
    </>
  );
}

/**
 * false will trigger a re-render
 */
export default memo(NexsetLoaderNode, (prev, next) => {
  const prevData = prev?.data;
  const nextData = next?.data;

  if (!isEqual(prevData?.source, nextData?.source)) return false;
  if (prevData?.layoutDirection !== nextData?.layoutDirection) return false;

  return (
    isEqual(prevData?.logsOnlyFocused, nextData?.logsOnlyFocused) &&
    isEqual(prevData?.selectedFlow, nextData?.selectedFlow) &&
    isEqual(prevData?.parentId, nextData?.parentId) &&
    prevData?.isSqlAutomation === nextData?.isSqlAutomation
  );
});
